import {
	Breakpoints,
	BreakpointWrapper,
	Container,
	ContainerGutter,
	ContainerPadding,
	Image,
	styled,
} from '@volkswagen-onehub/components-core';
import { default as React } from 'react';
import { RegisteredDisclaimer } from '../../helpers/get-disclaimer';
import {
	CarlineWithOfertas,
	Oferta,
	Promocion,
} from '../../services/ofertas-service/models/ofertas';
import ModelTitleBackground from '../model-title-background';
import { RedirectCTAs } from '../redirect-CTAs';
import { OfertaCardDescription } from './oferta-card-description';
import { OfertaCardEquipment } from './oferta-card-equipment';
import { OfertaCardTitle } from './oferta-card-title';
import { OfertaCardAccordionCta, OpenLayerCTA } from './oferta-CTAs';
import { OfertaPrice } from './oferta-price';

const OfertaInfoContainer = styled.div<{ isMobile: boolean }>`
	background-color: ${(props) => (props.isMobile ? 'linear-gradient(180deg, #f6f6f6 0%, #dfe4e8 100%)' : 'rgba(255, 255, 255, 0.7)')}};
	box-shadow: ${(props) => props.isMobile && '0px 3px 15px 0px rgba(0, 14, 38, 0.10), 0px 2px 4px 0px rgba(0, 14, 38, 0.20)'};
	backdrop-filter: blur(10px);
	border-radius: 10px;
	min-width: ${(props) => props.isMobile && '90vw'};
	height: ${(props) => props.isMobile && 'fit-content'};
`;

const MobileCtaContainer = styled.div`
	position: absolute;
	right: 20px;
`;

const OfertaCardImageContainer = styled.div`
	min-height: 180px;

	@media screen and (min-width: 560px) and (max-width: 979px) {
		min-height: 250px;
	}
`;

const OfertaCardImage = styled(Image)`
	@media screen and (max-width: 559px) {
		display: block;
		margin: 0 auto;
		width: 110%;
		height: auto;
		object-fit: cover;
		transform: translateX(-5%);
	}
`;

const OfertaCardAccordionCtaContainer = styled.div`
	height: 100%;
	align-content: center;
`;

export const OfertaCard = ({
	carline,
	isMobile,
	isLayerVisible,
	setIsLayerVisible,
	selectedOferta,
	setSelectedOferta,
	currentOferta,
	handleLayerOnClick,
	setSelectedPromotion,
	disclaimers,
}: {
	carline: CarlineWithOfertas;
	isMobile: boolean;
	isLayerVisible: boolean;
	setIsLayerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	selectedOferta: Oferta;
	setSelectedOferta: React.Dispatch<React.SetStateAction<Oferta>>;
	currentOferta: Oferta;
	className: string;
	setSelectedPromotion: React.Dispatch<
		React.SetStateAction<Promocion | undefined>
	>;
	handleLayerOnClick: () => void;
	disclaimers: RegisteredDisclaimer[];
}): React.ReactElement => {
	return (
		<OfertaInfoContainer className="oferta-info-container" isMobile={isMobile}>
			<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
				<ModelTitleBackground text={carline.title.replace(/-/g, '\u2011')} />
			</BreakpointWrapper>
			<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
				<OfertaCardImageContainer className="oferta-card-image-container">
					<OfertaCardImage
						className="car-image-mobile"
						src={currentOferta.mediaAssets.D6MOFA34FrontPC}
						alt={`${carline.title}`}
					/>
				</OfertaCardImageContainer>
			</BreakpointWrapper>
			<Container
				data-testid="carline-info-container"
				padding={{
					top: {
						[Breakpoints.b960]:
							currentOferta.id === selectedOferta.id
								? ContainerPadding.dynamic0100
								: ContainerPadding.dynamic0050,
					},
					bottom:
						currentOferta.id === selectedOferta.id
							? ContainerPadding.dynamic0100
							: ContainerPadding.dynamic0050,
					right: {
						[Breakpoints.default]: ContainerPadding.dynamic0100,
						[Breakpoints.b960]: ContainerPadding.dynamic0150,
						[Breakpoints.b1280]: ContainerPadding.dynamic0100,
					},
					left: {
						[Breakpoints.default]: ContainerPadding.dynamic0100,
						[Breakpoints.b960]: ContainerPadding.dynamic0150,
						[Breakpoints.b1280]: ContainerPadding.dynamic0100,
					},
				}}
				wrap={'always'}
				verticalAlign={'center'}
				horizontalAlign={'space-between'}
				stretchContent
			>
				<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
					<MobileCtaContainer className="mobile-cta-container">
						<OpenLayerCTA
							buttonSize="large"
							setIsLayerVisible={setIsLayerVisible}
							isLayerVisible={isLayerVisible}
							setSelectedOferta={setSelectedOferta}
							selectedOferta={currentOferta}
						/>
					</MobileCtaContainer>
					<OfertaCardTitle
						isMobile={isMobile}
						carline={carline}
						isLayerVisible={isLayerVisible}
						setIsLayerVisible={setIsLayerVisible}
						selectedOferta={selectedOferta}
						setSelectedOferta={setSelectedOferta}
						currentOferta={currentOferta}
						handleLayerOnClick={handleLayerOnClick}
						setSelectedPromotion={setSelectedPromotion}
					/>
				</BreakpointWrapper>
				<Container
					wrap={'never'}
					horizontalAlign={'space-between'}
					shrinkContent={{
						[Breakpoints.default]: true,
						[Breakpoints.b960]: false,
						[Breakpoints.b1280]: true,
					}}
					stretchContent={{
						[Breakpoints.default]: false,
						[Breakpoints.b960]: true,
					}}
				>
					<Container wrap={'always'} stretchContent>
						<BreakpointWrapper min={Breakpoints.b960}>
							<OfertaCardTitle
								className="oferta-card-title"
								carline={carline}
								isMobile={isMobile}
								isLayerVisible={isLayerVisible}
								setIsLayerVisible={setIsLayerVisible}
								selectedOferta={selectedOferta}
								setSelectedOferta={setSelectedOferta}
								currentOferta={currentOferta}
								handleLayerOnClick={handleLayerOnClick}
								setSelectedPromotion={setSelectedPromotion}
							/>

							{selectedOferta &&
								selectedOferta.id === currentOferta.id &&
								(currentOferta.titulo || currentOferta.descripcion) && (
									<OfertaCardDescription
										className="oferta-card-description"
										oferta={currentOferta}
									/>
								)}
							{selectedOferta && selectedOferta.id === currentOferta.id && (
								<OfertaCardEquipment
									className="oferta-card-equipment"
									oferta={currentOferta}
									numberOfOfertas={carline.ofertas.length}
								/>
							)}
						</BreakpointWrapper>
						<OfertaPrice oferta={currentOferta} disclaimers={disclaimers} />
					</Container>

					{selectedOferta && selectedOferta.id !== currentOferta.id && (
						<OfertaCardAccordionCtaContainer className="OfertaCardAccordionCtaContainer">
							<BreakpointWrapper
								data-testid="oferta-card-accordion-cta-2"
								min={Breakpoints.b960}
							>
								<OfertaCardAccordionCta
									currentOferta={currentOferta}
									selectedOferta={selectedOferta}
									setSelectedOferta={setSelectedOferta}
									isLayerVisible={isLayerVisible}
									setIsLayerVisible={setIsLayerVisible}
								/>
							</BreakpointWrapper>
						</OfertaCardAccordionCtaContainer>
					)}
				</Container>

				<BreakpointWrapper min={Breakpoints.default} max={Breakpoints.b960}>
					<Container
						data-testid="pide-mas-info-mobile"
						padding={{ top: ContainerPadding.static200 }}
						gutter={ContainerGutter.dynamic0200}
						verticalAlign="center"
						stretchContent
					>
						<RedirectCTAs
							copy={'Pide esta oferta'}
							emphasis="primary"
							oferta={selectedOferta}
							isMobile={true}
						/>
					</Container>
				</BreakpointWrapper>

				<BreakpointWrapper min={Breakpoints.b960}>
					{selectedOferta && selectedOferta.id === currentOferta.id && (
						<Container
							data-testid="pide-mas-info-desktop"
							padding={{ top: ContainerPadding.static200 }}
							gutter={ContainerGutter.dynamic0100}
							verticalAlign="center"
							stretchContent
						>
							<RedirectCTAs
								copy={'Pide esta oferta'}
								emphasis="primary"
								oferta={selectedOferta}
								isMobile={false}
							/>
						</Container>
					)}
				</BreakpointWrapper>
			</Container>
		</OfertaInfoContainer>
	);
};
